import { Component, OnDestroy, OnInit, effect, inject } from '@angular/core';
import { Router } from '@angular/router';
import { ToasterService, TranslateService } from '@devinforius/i-essentials';
import { INavbarConfig } from '@devinforius/i-essentials/lib/interfaces';
import { OAuthService } from 'angular-oauth2-oidc';

import { AuthService } from './core/services/authentication.service';
import { GlobalService } from '@core/services/global.service';

// Interface pour typer les données
import { UserData } from '@shared/types/user.type';
import { Language } from '@shared/types';
import { DOCUMENT } from '@angular/common';

@Component({
  standalone: false,
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent implements OnInit, OnDestroy {
  private readonly authService: AuthService = inject(AuthService);
  private readonly oAuthService: OAuthService = inject(OAuthService);
  private readonly router: Router = inject(Router);
  private readonly translater: TranslateService = inject(TranslateService);
  private readonly toaster: ToasterService = inject(ToasterService);
  private readonly globalService: GlobalService = inject(GlobalService);
  private readonly document: Document = inject(DOCUMENT);
  private isTranslationReady: boolean = false;
  private isUserReady: boolean = false;
  private userData$: import('rxjs').Subscription | undefined;
  private onTranslationReady$: import('rxjs').Subscription | undefined;
  private userData: UserData | null = null;

  public loading: boolean = true;
  public isConnected: boolean = false;
  public isConnectedEff = effect(() => {
    if (
      this.authService.isConnected() ||
      this.oAuthService.hasValidAccessToken()
    ) {
      this.isUserReady = true;

      this.checkAndSetNavBar();

      this.loading = false;
      this.isConnected = true;
      // Redirection vers l'accueil si l'utilisateur arrive sur la page callback
      if (this.router.url.startsWith('/callback')) {
        this.router.navigate(['home']);
      }
    }
  });
  public config!: INavbarConfig;

  public ngOnInit(): void {
    (async () => {
      await this.initTranslation();
      this.onTranslationReady$ = this.translater.onTranslationReady.subscribe(
        (value: boolean) => {
          if (value) {
            this.reloadApp();
            this.isTranslationReady = true;
            // S'assurer que l'attribut lang est correctement défini
            const currentLang = localStorage.getItem('lang') as Language;
            this.updateHtmlLangAttribute(currentLang);
            this.checkAndSetNavBar();
          }
        }
      );

      this.userData$ = this.globalService.userData$.subscribe(
        (userData: UserData | null) => {
          this.userData = userData;
          this.isUserReady = true;
          this.checkAndSetNavBar();
        }
      );
    })();
  }

  public ngOnDestroy(): void {
    this.isConnectedEff.destroy();
    this.userData$?.unsubscribe();
    this.onTranslationReady$?.unsubscribe();
  }

  public logout(): void {
    this.oAuthService.logOut();
  }

  private checkAndSetNavBar(): void {
    if (this.isTranslationReady && this.isUserReady) {
      this.setINavBarConfig();
    }
  }

  private setINavBarConfig(): void {
    if (!this.isTranslationReady || !this.isUserReady) {
      console.info('Conditions not met, returning early');
      return;
    }

    // Vérifier que les traductions sont réellement disponibles
    const testTranslation = this.translater.translate('app-navbar.home');
    if (testTranslation === 'app-navbar.home' || !testTranslation) {
      setTimeout(() => this.setINavBarConfig(), 100);
      return;
    }

    this.config = {
      names: {
        app: this.translater.translate('app_inforius'),
        home: 'BXL',
        user: this.userData?.userName ?? '',
      },
      links: {
        navLinks: [
          {
            icon: 'bi-house-door',
            title: this.translater.translate('app-navbar.home'),
            count: undefined,
            click: () => this.router.navigate(['home']),
          },
          {
            icon: 'bi-upload',
            title: this.translater.translate('app-navbar.import'),
            count: undefined,
            click: () => this.router.navigate(['import']),
          },
          {
            icon: 'bi-table',
            title: this.translater.translate('app-navbar.monitoring'),
            count: undefined,
            click: () => this.router.navigate(['monitoring']),
          },
          {
            icon: 'bi-search',
            title: this.translater.translate('app-navbar.search'),
            count: undefined,
            click: () => this.router.navigate(['search']),
          },
        ],
      },
      routes: { assistance: '/assistance' },
      menus: {
        userMenuItems: [],
        langMenuItems: [
          {
            icon: '../assets/flags/fr.svg',
            label: 'Français',
            command: () => {
              this.updateLanguage('fr');
            },
          },
          {
            icon: '../assets/flags/nl.svg',
            label: 'Nederlands',
            command: () => {
              this.updateLanguage('nl');
            },
          },
          {
            icon: '../assets/flags/en.svg',
            label: 'English',
            command: () => {
              this.updateLanguage('en');
            },
          },
          {
            icon: '../assets/flags/de.svg',
            label: 'Deutsch',
            command: () => {
              this.updateLanguage('de');
            },
          },
        ],
      },
      isSmallSidebar: false,
      appColor: '#FFF',
      showButtons: { admin: true, languages: true },
    };
  }

  private async updateLanguage(lang: Language): Promise<void> {
    this.loading = true;
    localStorage.setItem('lang', lang);
    await this.translater.setCurrentLang(lang);
    // Mettre à jour l'attribut lang du HTML
    this.updateHtmlLangAttribute(lang);
    // Forcer la mise à jour de la navbar
    setTimeout(() => {
      this.setINavBarConfig();
    }, 100);
    this.reloadApp();
  }

  private async initTranslation(): Promise<void> {
    this.loading = true;

    const lang: string | null = localStorage.getItem('lang');
    const allLanguages: Language[] = ['en', 'nl', 'fr', 'de'];

    if (!lang || lang === 'undefined' || lang === 'null') {
      const userLang: string = navigator.language
        .split('-')
        .reverse()[0]
        .toLowerCase();
      localStorage.setItem(
        'lang',
        allLanguages.indexOf(userLang as Language) >= 0 ? userLang : 'fr'
      );
    }

    await this.translater.init(
      localStorage.getItem('lang') as string,
      allLanguages,
      ['assets/translations', 'assets/i18n']
    );

    this.toaster.setKey('MyApp');
    // Ne pas appeler reloadApp() ici, attendre que les traductions soient prêtes
  }

  private reloadApp(): void {
    // on met le loading à false pour afficher le contenu de l'application
    this.loading = false;
  }

  private updateHtmlLangAttribute(lang: string): void {
    this.document.documentElement.lang = lang;
  }
}
